/**
 * =============================================================================
 * monthRangeSP - Utilitário para calcular o intervalo do mês corrente em SP
 *
 * Retorna [monthStart, monthEnd) em formato ISO (UTC), usados como filtro
 * `p_created_from/to` e `p_canceled_from/to` na RPC `wl_list_tenant_subscriptions`.
 * Mesma semântica de janela mensal usada em `wl_get_financial_kpis` (America/Sao_Paulo).
 *
 * @version 1.0.0
 * RELEASE: 2026-07-22 v1.0.0 - Criação inicial (alinha modal WL Financeiro aos KPIs mensais).
 * =============================================================================
 */

/** Extrai ano/mês do fuso America/Sao_Paulo para um Date qualquer. */
function ymdInSaoPaulo(d: Date): { year: number; month: number } {
  // Usa Intl para respeitar horário de verão SP historicamente aplicável.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(d);
  const year = Number(parts.find((p) => p.type === 'year')?.value);
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  return { year, month };
}

/**
 * Retorna o intervalo [start, end) do mês corrente em São Paulo,
 * já em ISO UTC pronto para enviar como TIMESTAMPTZ ao Postgres.
 * SP é UTC-3 (sem horário de verão desde 2019) → offset fixo.
 */
export function getCurrentMonthRangeSP(): { startISO: string; endISO: string; label: string } {
  return getMonthRangeSP(new Date());
}

/**
 * Versão parametrizada: calcula o intervalo [start, end) do mês contendo `ref`
 * (em fuso America/Sao_Paulo). Reaproveita a mesma semântica do range corrente.
 * Aceita null/undefined para retornar o mês corrente.
 *
 * @param ref Date arbitrário; quando ausente, usa `new Date()`.
 */
export function getMonthRangeSP(
  ref?: Date | null,
): { startISO: string; endISO: string; label: string } {
  const anchor = ref ?? new Date();
  const { year, month } = ymdInSaoPaulo(anchor);

  const startISO = new Date(
    `${year}-${String(month).padStart(2, '0')}-01T00:00:00-03:00`,
  ).toISOString();

  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const endISO = new Date(
    `${nextYear}-${String(nextMonth).padStart(2, '0')}-01T00:00:00-03:00`,
  ).toISOString();

  const label = new Intl.DateTimeFormat('pt-BR', {
    month: 'short',
    year: 'numeric',
    timeZone: 'America/Sao_Paulo',
  })
    .format(anchor)
    .replace('.', '');

  return { startISO, endISO, label };
}
